import { withBasePath } from '@/tenant/basePath.ts'

/**
 * Every route path in one place — no path strings typed inline in components.
 *
 * Each navigable state has its own URL so the platform Back button behaves
 * (MASTER.md §10, skill `ux` "Back Button", severity High). That includes every
 * booking step.
 */
export const paths = {
  home: '/',
  services: '/services',
  serviceDetail: '/services/:serviceSlug',
  contacts: '/contacts',
  account: '/account',
  install: '/install',
  booking: '/book/:serviceSlug',
  /** The token in the path IS the credential: never store or forward it. */
  myBooking: '/booking/:accessToken',
} as const

export type AppPath = (typeof paths)[keyof typeof paths]

/** Steps that exist as URLs under `/book/:serviceSlug`. */
export const bookingRouteSegments = ['date', 'time', 'contact', 'review', 'success'] as const

export type BookingRouteSegment = (typeof bookingRouteSegments)[number]

/** Builds a concrete service URL; keeps the `:serviceSlug` token in one place. */
export function serviceDetailPath(serviceSlug: string): string {
  return withBasePath(paths.serviceDetail.replace(':serviceSlug', serviceSlug))
}

/** `/book/<slug>` — entry point, redirects to the first incomplete step. */
export function bookingPath(serviceSlug: string): string {
  return withBasePath(paths.booking.replace(':serviceSlug', serviceSlug))
}

export function myBookingPath(accessToken: string): string {
  return withBasePath(paths.myBooking.replace(':accessToken', accessToken))
}

export function bookingStepPath(
  serviceSlug: string,
  segment: BookingRouteSegment,
): string {
  // bookingPath is already prefixed; do not prefix twice.
  return `${bookingPath(serviceSlug)}/${segment}`
}

/**
 * Owner cabinet.
 *
 * Not under `/s/<tenant>/owner/` yet — per-tenant addressing arrives with the
 * pipeline in stage 6, and moving these paths then is a rename, not a rewrite,
 * because every one of them is built here.
 */
export const ownerPaths = {
  root: '/owner',
  dashboard: '/owner',
  newBooking: '/owner/bookings/new',
  booking: '/owner/bookings/:bookingId',
  services: '/owner/services',
  photos: '/owner/photos',
  settings: '/owner/settings',
} as const

export function ownerBookingPath(bookingId: string): string {
  return withBasePath(ownerPaths.booking.replace(':bookingId', bookingId))
}


/**
 * A plain path turned into a link for the current studio.
 *
 * Components use this instead of the raw path constants, so a link written
 * once works on the legacy development routes and under /s/<slug>/ alike.
 */
export function link(path: string): string {
  return withBasePath(path)
}
